/**
 * Data layer for `affiliate-tiers`.
 *
 * Thin typed fetch wrappers over `/api/v1/affiliate-tiers`. Raw `fetch()`
 * is confined to this file per the dash contract (D6).
 *
 * BE endpoints:
 *   GET    /                            — list tiers ({ tiers: View[] })
 *   POST   /                            — create tier ({ tier: View })
 *   PATCH  /:id                         — update tier
 *   DELETE /:id                         — remove tier ({ deleted, id })
 *   POST   /assign                      — manual assignment
 *   POST   /recompute/:affiliate_id     — recompute 30d
 *   GET    /assignments/:affiliate_id   — assignment history
 */

export interface AffiliateTierView {
	id: string;
	name: string;
	min_conversions: number;
	min_revenue_cents: number;
	payout_multiplier_bps: number;
	payout_multiplier: number;
	color: string;
	sort_order: number;
	created_at: number;
	updated_at: number;
}

export interface AffiliateTierAssignmentView {
	id: string;
	affiliate_id: string;
	tier_id: string;
	assigned_at: number;
	reason: string;
}

export interface ListAffiliateTiersResponse {
	tiers: AffiliateTierView[];
}

export interface CreateAffiliateTierBody {
	name: string;
	minConversions: number;
	minRevenueCents: number;
	payoutMultiplierBps: number;
	color: string;
	sortOrder: number;
}

export interface CreateAffiliateTierResponse {
	tier: AffiliateTierView;
}

export interface DeleteAffiliateTierResponse {
	deleted: boolean;
	id: string;
}

const BASE = "/api/v1/affiliate-tiers";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAffiliateTiersResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAffiliateTiersResponse>(res);
}

export async function create(
	body: CreateAffiliateTierBody,
): Promise<CreateAffiliateTierResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateAffiliateTierResponse>(res);
}

export async function remove(id: string): Promise<DeleteAffiliateTierResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<DeleteAffiliateTierResponse>(res);
}

export const affiliateTiersQueryKeys = {
	all: ["affiliate-tiers"] as const,
	list: () => [...affiliateTiersQueryKeys.all] as const,
	detail: (id: string) => [...affiliateTiersQueryKeys.all, id] as const,
};

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit testing. No network calls.
// ---------------------------------------------------------------------------

/** Convert a float multiplier (1.0..2.0) into integer basis points. */
export function multiplierToBps(m: number): number {
	if (!Number.isFinite(m)) return 10_000;
	return Math.round(m * 10_000);
}

/** Format an integer bps value as a scaled multiplier string, e.g. "1.25x". */
export function formatMultiplier(bps: number): string {
	const m = bps / 10_000;
	return `${m.toFixed(2)}x`;
}

/** Format an integer cents value as "$X.YY". */
export function formatRevenueCents(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const whole = Math.floor(abs / 100);
	const frac = (abs % 100).toString().padStart(2, "0");
	return `${sign}$${whole}.${frac}`;
}

/** Sort tiers for presentation: sort_order ASC, revenue ASC, id ASC. */
export function sortTiersForDisplay(
	tiers: readonly AffiliateTierView[],
): AffiliateTierView[] {
	return tiers.slice().sort((a, b) => {
		if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
		if (a.min_revenue_cents !== b.min_revenue_cents) {
			return a.min_revenue_cents - b.min_revenue_cents;
		}
		return a.id.localeCompare(b.id);
	});
}
